import { useEffect, useState } from 'react';
import axios from 'axios';
import { isEmpty, isNull, cloneDeep } from 'lodash';
import { connect } from 'react-redux';
import {
    getPersistedState,
    setPersistedState,
} from '../actions/persistedStateActions';
import { setSearchResultsLoading, resetSearchPageGlobalState } from '../actions/searchActions';
import { getRole } from '../data/getRole';
import { formatSearchLocationParam } from '../data/searchHelper';
import {
    API_KEYS,
    PERSISTED_STATE_SEARCH_LISTINGS,
    CONTACT, PERSISTED_STATE_SEARCH_PAGE,
} from '../__constants__';

import { mapEmptyArr } from '../utils/utilities';
import IndividualTileSkeleton from '../components/FormComponents/Skeletons/IndividualTileSkeleton';

export const STATS_URL = `${API_KEYS.API_URL}/v2/public/stats-full`;
export const STATS_URL_INTERNAL = `${API_KEYS.API_URL_INTERNAL}/v2/public/stats-full`;

export function createRequestConfig(params) {
    return {
        params,
    };
}

function createInitialState(props) {
    const persistedSearchPage = getPersistedState(PERSISTED_STATE_SEARCH_PAGE);
    const persistedSearchListings = getPersistedState(PERSISTED_STATE_SEARCH_LISTINGS);

    //server side does not have access to local storage, it returns the first 5 results with default filters
    //if we have saved filters/results in local storage the search will have to be repeated with saved filter data
    const initialStateMatch =  persistedSearchListings && persistedSearchPage && JSON.stringify(persistedSearchListings.data) === JSON.stringify(props.initialProfessionals.data)

    if (initialStateMatch) {
        if (props.urlPath === persistedSearchPage.urlPath) {
            return {...persistedSearchListings};
        }
    }

    return {
        initialised: true,
        data: props.initialProfessionals,
        sortBy: 'sigmoid',
        limit: 5,
        error: false,
        scrollYPos: 0,
        reinit: !initialStateMatch
    };
}

export function withSearchPageListings(WrappedComponent) {
    function SearchPageListingsHOC(props) {
        const initialState = createInitialState(props)

        const [isInitialised] = useState(initialState.initialised)
        const [data, setData] = useState(initialState.data)
        const [sortBy, setSortBy] = useState(initialState.sortBy)
        const [limit, setLimit] = useState(initialState.limit)
        const [error, setError] = useState(initialState.error)
        const [scrollYPos, setScrollYPos] = useState(initialState.scrollYPos)
        const [reinit, setReinit] = useState(initialState.reinit)

        useEffect(() => {
            if (reinit) {
                handleLoading()
            }

            return () => {
                props.resetSearchPageGlobalState()
            }
        }, [])

        useEffect(() => {
            const stateToPersist = {
                initialised: isInitialised,
                data,
                sortBy,
                limit,
                error,
                scrollYPos,
                reinit
            }

            setPersistedState(PERSISTED_STATE_SEARCH_LISTINGS, stateToPersist);
        }, [isInitialised, data, sortBy, limit, error, scrollYPos, reinit])

        useEffect(() => {
            if (isInitialised) {
                handleLoading();
            }
        }, [props.defaultParams, sortBy, limit, scrollYPos])

        const handleSortByChange = e => {
            setSortBy(e.target.value)
        }

        const handleNextPageChange = e => {
            setLimit(limit + 5)
            setScrollYPos(window.pageYOffset)
        }

        const handleLoading = () => {
            const defaultParams = cloneDeep(props.defaultParams);

            if (isEmpty(defaultParams)) {
                setData({})

                props.setSearchResultsLoading(false);

                return;
            }

            props.setSearchResultsLoading(true);

            defaultParams.limit = limit;
            defaultParams.sort_by = sortBy;

            if (defaultParams.location) {
                defaultParams.location = formatSearchLocationParam(defaultParams.location);
            }

            axios.get(STATS_URL, createRequestConfig(defaultParams))
                .then(response => {
                    const { data } = response;

                    if (data) {
                        setData(data)
                        setReinit(false)

                        if (0 < scrollYPos) {
                            window.scrollTo(0, scrollYPos);
                        }
                    }
                })
                .then(() => handleSearchComplete(data.data))
                .catch(error => {
                    setError(error.response);
                    props.setSearchResultsLoading(false);
                });
        }

        const handleSearchComplete = searchData => {
            props.hasSearchResults(!isEmpty(searchData));
            props.setSearchResultsLoading(false);
        }

        const { vertical_id } = props.defaultParams;

        return props.loading
            ? <div>
                { mapEmptyArr(limit, (i) => <IndividualTileSkeleton key={i} />) }
            </div>
            : isNull(data) || error
                ? (
                    <div className='text-center mt-10'>
                        <p className='text-primary-blue text-[1.1rem] font-normal leading-[1.5] m-0'>
                            <strong>
                                We're sorry, but something went wrong while we were finding your matches. Please try refreshing the page, or call our support team on { CONTACT.ACCOUNTS_TEAM_NUMBER }.
                            </strong>
                        </p>
                    </div>
                )
                : (
                    <WrappedComponent
                        data={data}
                        firstName={props.defaultParams.fn}
                        secondName={props.defaultParams.ln}
                        searchType={props.searchType}
                        location={props.defaultParams.location}
                        sortBy={sortBy}
                        handleChange={handleNextPageChange}
                        handleSortByChange={handleSortByChange}
                        verticalId={vertical_id}
                        role={getRole(vertical_id, props.defaultParams.attribute_id || '')}
                        servicesList={props.servicesList}
                    />
                )
    }

    const mapStateToProps = ({ search }) => {
        return {
            loading: search.loading,
        };
    };

    const actions = {
        setSearchResultsLoading,
        resetSearchPageGlobalState,
    };

    return connect(mapStateToProps, actions)(SearchPageListingsHOC);
}
